import { heroDelivery, heroPreloadLinks } from "@/lib/hero-delivery";
import { deliveryVariants, imageSrcSet } from "@/lib/image-delivery";
import { LocaleLink, type PublicPath } from "@/components/site/LocaleLink";
import { createFileRoute } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { PageHero } from "@/components/site/PageHero";
import { Section } from "@/components/site/Section";
import { Overline } from "@/components/site/Overline";
import { ArrowRight, Clock } from "lucide-react";
import blogHero from "@/assets/blog/blog-hero.avif";
import blogHeroSmall from "@/assets/blog/blog-hero-640.avif";
import { publishedPostsQuery } from "@/lib/blog-queries";
import { useSuspenseQuery } from "@tanstack/react-query";
import { canonical, breadcrumbJsonLd, SITE_URL, shareImageMeta, langPageMeta, business, responseTime } from "@/content/site";
import { publishedPageQuery } from "@/lib/content-queries";
import { pageContent } from "@/lib/page-content";
import { usePageContent, validateVoorvertoning } from "@/lib/use-page-content";
import { PhotoBlocks } from "@/components/site/PhotoBlocks";

const PATH = "/blog";
const SLEUTEL = "blog";

const HERO_FALLBACK = { src: blogHero, srcSet: `${blogHeroSmall} 640w, ${blogHero} 1280w` };

const CODE_META_TITLE = "Zwembad blog: kennis, onderhoud en techniek";
const CODE_META_DESCRIPTION =
  "Praktische artikels over zwembaden bouwen, renoveren, verwarmen en onderhouden. Advies van Gopro Projects uit Maasmechelen voor Limburg en Zuid-Nederland.";

export const Route = createFileRoute("/$lang/blog/")({
  staticData: { sitemap: true },
  validateSearch: validateVoorvertoning,
  loader: async ({ context, params }) => {
    const [posts, page] = await Promise.all([
      context.queryClient.ensureQueryData(publishedPostsQuery),
      context.queryClient.ensureQueryData(publishedPageQuery(SLEUTEL, params.lang)),
    ]);
    return { posts, page };
  },
  head: ({ loaderData, params }) => {
    const c = pageContent(loaderData?.page);
    const title = c.metaTitle(CODE_META_TITLE);
    const description = c.metaDescription(CODE_META_DESCRIPTION);
    const heroPhoto = heroDelivery(c.section("hero"), HERO_FALLBACK);
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        {
          property: "og:description",
          content: "Artikels over zwembadbouw, renovatie, waterbehandeling, verwarming en onderhoud per seizoen.",
        },
        { property: "og:type", content: "website" },
        ...langPageMeta(params.lang, PATH).meta,
        { name: "twitter:card", content: "summary_large_image" },
        ...shareImageMeta("blog", "Zwembadkennis en adviesartikels van Gopro Projects"),
      ],
      links: [...langPageMeta(params.lang, PATH).links, ...heroPreloadLinks(heroPhoto)],
      scripts: [
        breadcrumbJsonLd("Blog", PATH),
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            name: "Gopro Projects zwembadblog",
            url: canonical(PATH),
            inLanguage: "nl-BE",
            publisher: { "@id": `${SITE_URL}/#organization` },
            blogPost: (loaderData?.posts ?? []).map((post) => ({
              "@type": "BlogPosting",
              headline: post.title,
              url: `${SITE_URL}/blog/${post.slug}`,
              datePublished: post.datePublished,
              description: post.metaDescription,
            })),
          }),
        },
      ],
    };
  },
  component: BlogIndexPage,
});

function BlogIndexPage() {
  const { lang } = Route.useParams();
  const { voorvertoning } = Route.useSearch();
  const c = usePageContent(SLEUTEL, lang, voorvertoning);
  const { data: posts } = useSuspenseQuery(publishedPostsQuery);
  const categories = [...new Set(posts.map((post) => post.category))];
  const [featured, ...rest] = posts;
  const hero = c.section("hero");
  const heroPhoto = heroDelivery(hero, HERO_FALLBACK);

  return (
    <div>
      <PageHero
        title={c.veld("hero", "titel", "Zwembadkennis van een zwembadbouwer")}
        subtitle={c.rijkInline("hero",
          "Heldere artikels over bouwen, renoveren, verwarmen en onderhouden van zwembaden. Geschreven op basis van wat wij dagelijks op de werf zien in Limburg en Zuid-Nederland.",
        )}
        imageSrc={heroPhoto.src}
        imageFocal={heroPhoto.focal}
        imageSrcSet={heroPhoto.srcSet}
        imageAlt={c.veld(
          "hero",
          "alt",
          "Zwembad met lichtgrijze bekleding en verlichting in een tuin bij valavond",
        )}
        cta={{
          label: c.veld("hero", "knopTekst", "Offerte aanvragen"),
          to: c.veld("hero", "knopLink", "/offerte") as PublicPath,
        }}
        secondaryCta={{
          label: c.veld("hero", "knop2Tekst", "Onze realisaties"),
          to: c.veld("hero", "knop2Link", "/realisaties") as PublicPath,
        }}
      />

      <Section tone="light" className="overflow-hidden">
        {posts.length === 0 ? (
          <p className="text-[17px] leading-7 text-on-surface-variant">
            {c.rijkInline("leeg", "Er staan nog geen artikels online. Kom binnenkort gerust terug.")}
          </p>
        ) : (
          <>
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <span
                  key={category}
                  className="rounded border border-border px-4 py-1.5 text-sm font-medium text-on-surface-variant"
                >
                  {category}
                </span>
              ))}
            </div>

            {featured && (
              <article className="mt-12 grid gap-0 md:grid-cols-[1.15fr_0.85fr]">
                <img
                  src={featured.image}
                  srcSet={imageSrcSet(deliveryVariants(featured.image, featured.imageVariants))}
                  sizes="(min-width: 768px) 55vw, 100vw"
                  loading="lazy"
                  decoding="async"
                  alt={featured.imageAlt}
                  width={1280}
                  height={720}
                  className="min-h-80 h-full w-full object-cover"
                />
                <div className="flex flex-col justify-center space-y-5 bg-primary p-8 text-primary-foreground lg:p-12">
                  <Overline onDark>{featured.category}</Overline>
                  <h2 className="display-section">
                    <LocaleLink to="/blog/$slug" params={{ slug: featured.slug }} className="hover:text-secondary">
                      {featured.title}
                    </LocaleLink>
                  </h2>
                  <p className="text-[17px] leading-7 text-primary-foreground/80">{featured.excerpt}</p>
                  <p className="inline-flex items-center gap-2 text-sm text-primary-foreground/70">
                    <Clock className="h-4 w-4" strokeWidth={1.75} /> {featured.readingMinutes} min lezen
                  </p>
                  <Button asChild className="self-start bg-secondary text-secondary-foreground hover:bg-secondary/90">
                    <LocaleLink to="/blog/$slug" params={{ slug: featured.slug }}>
                      Lees het artikel <ArrowRight className="ml-2 h-4 w-4" />
                    </LocaleLink>
                  </Button>
                </div>
              </article>
            )}

            <div className="mt-12 grid gap-x-3 gap-y-12 md:grid-cols-2 lg:grid-cols-3">
              {rest.map((post) => (
                <article key={post.slug} className="group flex flex-col overflow-hidden border-b border-border pb-8">
                  <img
                    src={post.image}
                    srcSet={imageSrcSet(deliveryVariants(post.image, post.imageVariants))}
                    sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
                    alt={post.imageAlt}
                    width={1280}
                    height={720}
                    loading="lazy"
                    className="aspect-[4/3] w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.02]"
                  />
                  <div className="flex flex-1 flex-col gap-3 pt-6">
                    <Overline>{post.category}</Overline>
                    <h3 className="font-display text-xl font-bold leading-tight">
                      <LocaleLink to="/blog/$slug" params={{ slug: post.slug }} className="hover:text-brand-deep">
                        {post.title}
                      </LocaleLink>
                    </h3>
                    <p className="flex-1 text-sm leading-6 text-on-surface-variant">{post.excerpt}</p>
                    <p className="inline-flex items-center gap-2 text-xs text-on-surface-variant">
                      <Clock className="h-3.5 w-3.5" strokeWidth={1.75} /> {post.readingMinutes} min lezen
                    </p>
                    <LocaleLink
                      to="/blog/$slug"
                      params={{ slug: post.slug }}
                      className="inline-flex items-center gap-2 text-sm font-semibold text-brand-deep hover:underline"
                    >
                      Lees verder <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
                    </LocaleLink>
                  </div>
                </article>
              ))}
            </div>
          </>
        )}
      </Section>

      <Section tone="navy">
        <div className="grid gap-8 lg:grid-cols-[0.8fr_1.2fr] lg:items-end">
          <div>
            <Overline onDark>{c.veld("contact", "overline", "Contact")}</Overline>
            <h2 className="mt-4 display-section">
              {c.veld("contact", "titel", "Een vraag over uw eigen zwembad?")}
            </h2>
          </div>
          <div className="border-l border-primary-foreground/20 pl-6 lg:pl-10">
            <p className="text-[17px] leading-7 text-primary-foreground/80">
              {c.rijkInline("contact",
                `Bel ons op ${business.phone} of vraag vrijblijvend een offerte aan. Wij antwoorden ${responseTime.within} en komen ter plaatse in heel Limburg en Zuid-Nederland.`,
              )}
            </p>
            <Button asChild size="lg" className="mt-8 bg-secondary text-secondary-foreground hover:bg-secondary/90">
              <LocaleLink to={c.veld("contact", "knopLink", "/offerte") as PublicPath}>
                {c.veld("contact", "knopTekst", "Offerte aanvragen")}
              </LocaleLink>
            </Button>
          </div>
        </div>
      </Section>
      <PhotoBlocks content={c} />
    </div>
  );
}
